import {Component, HostListener, ViewEncapsulation} from '@angular/core';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {FormControl} from "@angular/forms";

import {cloneDeep, orderBy} from 'lodash';
import { FamilyRoomDirectoryService } from "@services/family-room-directory.service";
import { debounceTime } from "rxjs/internal/operators/debounceTime";
import { take } from "rxjs/internal/operators/take";
import { AutocompleteService } from "@services/autocomplete.service";
import { NgxPermissionsObject, NgxPermissionsService } from "ngx-permissions";

@Component({
  selector: 'family-room-directory',
  standalone: false,
  templateUrl: './family-room-directory.component.html',
  styleUrl: './family-room-directory.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class FamilyRoomDirectoryComponent {
  activeFilter: number = -1;
  excludeHiddenPeople: boolean = true;
  filterString: string = '';
  isEditing: boolean = false;
  isMobile: boolean = false;
  perms: NgxPermissionsObject;
  personEditing;
  title = 'family-app';

  people = [];

  affiliationControl = new FormControl();
  filteredAffiliations = [];
  isAffiliationValid: boolean = true;
  isEntitiesExpanded: boolean = true;
  isNameValid: boolean = true;
  isRelationshipValid: boolean = true;
  isGeneralExpanded: boolean = true;
  isRelationshipCategoryExpanded: boolean = true;

  readonly familyFriendOption = 0;
  readonly staffOption = 1;

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: any) {
    if ('Escape' == event.key) {
      this.stopEditing();
    }
  }

  constructor(protected familyRoomDirectoryService: FamilyRoomDirectoryService,
              protected autocompleteService: AutocompleteService,
              protected breakpointObserver: BreakpointObserver,
              private permissionsService: NgxPermissionsService,
              ) {
    this.getPeople();
  }

  ngOnInit() {
    const layoutChanges = this.breakpointObserver.observe([
      Breakpoints.XSmall, Breakpoints.Small,
    ]);

    layoutChanges.subscribe((result) => {
      this.isMobile = result.matches;
    });
    this.perms = this.permissionsService.getPermissions();

    this.affiliationControl.valueChanges.pipe(
      debounceTime(200),
    ).subscribe((affils: any) => {
      this.filterAffiliations(affils);
    });
  }

  get filteredPeople() {
    let filteredPeople = [];

    const lowerFilterString = this.filterString.toLowerCase();

    if (this.activeFilter === -1) {
      filteredPeople = [...this.people];
    } else if (this.activeFilter === this.familyFriendOption) {
      filteredPeople = this.people.filter((person) => {
        return person.isFamilyFriend;
      })
    } else if (this.activeFilter === this.staffOption) {
      filteredPeople = this.people.filter((person) => {
        return person.isStaff;
      })
    }

    if (this.filterString.length > 0) {
      filteredPeople = filteredPeople.filter((person) => {
        return person.name.toLowerCase().includes(lowerFilterString) || person.description?.toLowerCase().includes(lowerFilterString)
          || person.affiliationName?.toLowerCase().includes(lowerFilterString);
      })
    }

    if (this.activeFilter === this.familyFriendOption) {
      filteredPeople = orderBy(filteredPeople, ['affiliationLastName', 'affiliationName', 'name']);
    } else {
      filteredPeople = orderBy(filteredPeople, ['name']);
    }

    return filteredPeople;
  }

  get isPersonEditingValid() {
    let isValid = true;

    if (!this.personEditing.firstNameDisplay?.length || !this.personEditing.lastNameDisplay?.length) {
      this.isNameValid = false;
      isValid = false;
    }

    if (!this.personEditing.relationshipTypeID) {
      this.isRelationshipValid = false;
      isValid = false;
    }

    if (!this.personEditing.affiliationID) {
      this.isAffiliationValid = false;
      isValid = false;
    }

    return isValid;
  }

  filterAffiliations(searchSting: string) {
    if (!searchSting?.length) {
      this.filteredAffiliations = [];
      return;
    }
    this.autocompleteService.getElasticSearchResults(searchSting, 'Person', 10, 'NBA', '1').pipe(
      take(1),
    ).subscribe(entities => {
      this.filteredAffiliations = entities;
    });
  }

  getPeople() {
    this.familyRoomDirectoryService.getFamilyRoomDirectory().subscribe((people) => {
      this.people = orderBy(people, ['name']);
    })
  }

  addEntity() {
    const person = {showInFamilyRoom: true};
    this.startEditing(person);
  }

  startEditing(person): void {
    this.personEditing = cloneDeep(person);
    this.isEditing = true;
  }

  stopEditing(): void {
    this.isEditing = false;
    this.personEditing = null;
    this.markValid();
  }

  markValid() {
    this.isAffiliationValid = true;
    this.isNameValid = true;
    this.isRelationshipValid = true;
  }

  saveUpdates(): void {
    if (!this.isPersonEditingValid) {
      return;
    }
    this.familyRoomDirectoryService.updateFamilyRoomIndividual(this.personEditing).subscribe((_) => {
      this.people = [];
      this.getPeople();
      this.stopEditing();
    });
  }

  updateRelationshipType(relationshipType) {
    this.personEditing.relationshipTypeID = relationshipType.value;
  }

  updateActiveFilter(filterID: number) {
    if (this.activeFilter === filterID) {
      this.activeFilter = -1;
    } else {
      this.activeFilter = filterID;
    }
  }

  removeAffiliation() {
    this.personEditing.affiliationLastName = null;
    this.personEditing.affiliationName = null;
    this.personEditing.affiliationID = null;
  }

  addAffiliation(event) {
    const value = event.option.value;
    this.personEditing.affiliationName = value.name;
    this.personEditing.affiliationLastName = value.lastName;
    this.personEditing.affiliationID = value.id;
  }
}
